import {createContext, useCallback, useContext, useState, type ReactNode} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {setViewAsPartnerId} from '@/lib/api'

export type ViewAsPartnerTarget = {userId: string; name: string}

type ViewAsPartner = {
  target: ViewAsPartnerTarget | null
  /** Solo para usuarios de sistema. */
  viewAsPartner: (target: ViewAsPartnerTarget) => void
  exit: () => void
}

const ViewAsPartnerContext = createContext<ViewAsPartner>({
  target: null,
  viewAsPartner: () => {},
  exit: () => {},
})

/** Lente de solo lectura (lo impone el backend). Es global porque el panel del partner cruza
 * empresas: no vive dentro de un tenant. */
export function ViewAsPartnerProvider({children}: {children: ReactNode}) {
  const queryClient = useQueryClient()
  const [target, setTarget] = useState<ViewAsPartnerTarget | null>(null)

  const viewAsPartner = useCallback(
    (next: ViewAsPartnerTarget) => {
      // Set the header BEFORE touching the cache, so the invalidation's refetch
      // already rides the target's lens (never a stray own-view read).
      setViewAsPartnerId(next.userId)
      setTarget(next)
      void queryClient.invalidateQueries()
    },
    [queryClient]
  )

  const exit = useCallback(() => {
    setViewAsPartnerId(null)
    setTarget(null)
    // Quitar y no invalidar: invalidar refetchearía las consultas montadas ya sin el header
    // y daría 403 antes de navegar.
    queryClient.removeQueries({queryKey: ['partner']})
  }, [queryClient])

  return <ViewAsPartnerContext.Provider value={{target, viewAsPartner, exit}}>{children}</ViewAsPartnerContext.Provider>
}

export function useViewAsPartner(): ViewAsPartner {
  return useContext(ViewAsPartnerContext)
}
